<template>
  <div class="job_info_wrapper p-0">
    <div class="rate_shifts_info">
      <div class="info_wrapper d-flex align-items-center">
        <ClockIcon />
        <div class="info_title">
          Available Shifts
          <span>{{ checkJobDateStart(job.startDate, job.endDate) }}</span>
        </div>
      </div>
      <div class="info_wrapper d-flex align-items-center" v-if="showRateInfo">
        <DollarWithCircleIcon />
        <div class="d-flex flex-wrap gap-y-2">
          <div class="info_title">Hourly rate <template v-if="isAustralia">(excluding super)</template></div>
          <div class="info_value">
            {{ getHourlyRate() }}
          </div>
        </div>
      </div>
      <div class="info_wrapper d-flex align-items-center" v-if="userRole === 'pharmacy' && showTravelInfo">
        <div class="d-flex flex-wrap gap-y-2">
          <TravelIcon />
          <div class="d-flex flex-wrap gap-y-2">
            <div class="info_title">Capped Travel</div>
            <div class="info_value">
              <span>{{ formatCurrency(job.maxTravelCompensation) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="info-separator"></div>

    <!-- @change="handleChange" -->
    <el-collapse accordion v-if="job.settings !== null" class="job-about-collapse">
      <el-collapse-item class="card-accordeon" title="More about the job" name="first">
        <div class="collapse-list more-info">
          <div class="collapse-item" v-if="showRateInfo">
            <span class="collapse-title">Hourly Rate <template v-if="isAustralia">(excluding super)</template></span>
            <span class="collapse-value">
              {{ getHourlyRate() }}
            </span>
          </div>
          <div v-if="job.settings.languages" class="collapse-item">
            <span class="collapse-title">Language</span>
            <span class="collapse-value">
              {{ job.settings.languageNames }}
            </span>
          </div>
          <div class="collapse-item">
            <span class="collapse-title">Solo {{ getSystemLocumName(false, true) }}</span>
            <span class="collapse-value">
              {{ job.settings.solo == 1 ? "Yes" : "No" }}
            </span>
          </div>
          <div class="collapse-item" v-if="brandName !== 'HEYLUCY'">
            <span class="collapse-title">
              {{ message.Immuniser }} req?
            </span>
            <span class="collapse-value">
              {{ job.covidCertRequired == 0 ? "Not Required" : job.covidCertRequired == 1 ? "Mandatory" : "Nice to Have" }}
            </span>
          </div>
          <div class="collapse-item" v-if="brandName !== 'HEYLUCY'">
            <span class="collapse-title">Number of technicians</span>
            <span class="collapse-value">{{ job.settings.numOfTech }}</span>
          </div>
          <template v-if="showRateInfo">
            <div class="collapse-item" v-if="showTravelInfo">
              <span class="collapse-title">Travel</span>
              <span class="collapse-value">{{ job.enableTravelAllowanceForFirstAndLastDay === 1 ? "Pay for first and last day of shift" : "Capped Travel" }}</span>
            </div>
            <div class="collapse-item" v-else>
              <span class="collapse-title">Pay for Travel
                {{
                  job.settings.payingForTravel == 1
                    ? " (>50KM roundtrip)"
                    : ""
                }}
              </span>
              <span class="collapse-value">{{
                job.enableTravelAllowanceForFirstAndLastDay === 1
                  ? "Pay for first and last day of shift"
                  : job.settings.payingForTravel === 0
                    ? "No travel"
                    : job.settings.payingForTravel === 1
                      ? "Partial travel"
                      : "Full travel"
              }}</span>
            </div>
          </template>
          <div class="collapse-item">
            <span class="collapse-title" >Pay for Flights</span>
            <span class="collapse-value">
              {{ job.settings.payForFlights == 0 ? "No" : "Yes" }}
            </span>
          </div>
          <div class="collapse-item">
            <span class="collapse-title">Pay for Accommodation</span>
            <span class="collapse-value">
              {{ job.settings.payForAccommodation == 0 ? "No" : "Yes" }}
            </span>
          </div>
          <div class="collapse-item" v-if="isAustralia && brandName !== 'HEYLUCY'">
            <span class="collapse-title">Dosage Administration Aids</span>
            <span class="collapse-value">
              {{ job.settings.adminAids == 1 ? "Yes" : "No" }}
            </span>
          </div>
          <div class="collapse-item" v-if="brandName !== 'HEYLUCY'">
            <span class="collapse-title">Open Pharmacy</span>
            <span class="collapse-value">
              {{ job.settings.openUp == 1 ? "Yes" : "No" }}
            </span>
          </div>
          <div class="collapse-item" v-if="brandName !== 'HEYLUCY'">
            <span class="collapse-title">Close Pharmacy</span>
            <span class="collapse-value">
              {{ job.settings.close == 1 ? "Yes" : "No" }}
            </span>
          </div>
          <div v-if="job.settings.notes" class="collapse-item collapse-item--notes">
            <span class="collapse-title">Notes</span>
            <p class="job-notes__content">{{ job.settings.notes }}</p>
          </div>
          <div v-if="job.settings.parking" class="collapse-item collapse-item--notes">
            <span class="collapse-title">Parking</span>
            <p class="job-notes__content">{{ job.settings.parking }}</p>
          </div>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script setup>
import { inject, getCurrentInstance, computed } from "vue";
import moment from "moment";
import { ClockIcon, DollarWithCircleIcon, TravelIcon } from "@/components/Icons";
import { locumCanSeeJobRates } from "@/helpers/utility/locumShiftRates";

// eslint-disable-next-line no-undef
const props = defineProps({
  job: {
    type: Object,
    required: true,
  },
  userRole: {
    type: String,
    required: true,
  },
  isAustralia: {
    type: Boolean,
    required: true,
  },
  shifts: {
    type: Array,
    default: null,
  },
  shiftsComplete: {
    type: Boolean,
    default: true,
  },
});

const instance = getCurrentInstance();
const message = inject("messages");
const $filters = instance?.appContext?.config?.globalProperties?.$filters || {};

const brandName= process.env.VUE_APP_BRAND;

// Helper function to access $filters
const getSystemLocumName = (capitalizeFirstLetter, capitalizeBothWords) => {
  return $filters?.systemLocumName ? $filters.systemLocumName(capitalizeFirstLetter, capitalizeBothWords) : '';
};

const checkJobDateStart = (dateStart, endDate, trim) => {
  let start = moment(dateStart, "YYYY/MM/DD");
  let end = moment(endDate, "YYYY/MM/DD");
  if (start.format("MMMM") == end.format("MMMM")) {
    if (start.format("D") == end.format("D")) {
      return start.format("D " + (trim ? "MMM" : "MMMM"));
    }
    return (
      start.format("D ") +
      " - " +
      end.format("D " + (trim ? "MMM" : "MMMM"))
    );
  } else {
    return (
      start.format("D " + (trim ? "MMM" : "MMMM")) +
      " - " +
      end.format("D " + (trim ? "MMM" : "MMMM"))
    );
  }
};

const formatCurrency = (rate) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(rate);
};

const getHourlyRate = () => {
  if (props.userRole === 'pharmacy') {
    return formatHourlyRatePharmacy();
  }
  return formatHourlyRate();
};

const formatHourlyRate = () => {
  const regularRate = props.job.recommendedRate;
  const weekendRate = props.job.recommendedWeekendRate;
  // TODO:: After optimization we do not have shift in job 
  // if (
  //   props.job.shifts.length === 1 &&
  //   props.job.shifts[0].payRateExecSuper
  // ) {
  //   return new Intl.NumberFormat("en-AU", {
  //     style: "currency",
  //     currency: "AUD",
  //   }).format(
  //     props.job.shifts[0].payRate - props.job.shifts[0].payRateSuper
  //   );
  // }
  let formattedString = "";
  formattedString += regularRate ? formatCurrency(regularRate) : "";
  formattedString += regularRate && weekendRate ? " - " : "";
  formattedString += weekendRate ? formatCurrency(weekendRate) : "";

  return formattedString;
}

const formatHourlyRatePharmacy = () => {
  const rateArray = [];
  // TODO:: After optimization we do not have shift in job 
  // if (
  //   props.job.shifts.length === 1 &&
  //   props.job.shifts[0].payRateExecSuper
  // ) {
  //   return new Intl.NumberFormat("en-AU", {
  //     style: "currency",
  //     currency: "AUD",
  //   }).format(
  //     props.job.shifts[0].payRate - props.job.shifts[0].payRateSuper
  //   );
  // }

  if (props.job.recommendedRatesString) {
    return props.job.recommendedRatesString;
  }

  if (props.job.minRate) {
    rateArray.push(props.job.minRate);
  }
  if (props.job.maxRate) {
    rateArray.push(props.job.maxRate);
  }
  if (props.job.weekendMaxRate) {
    rateArray.push(props.job.weekendMaxRate);
  }
  if (props.job.weekendMinRate) {
    rateArray.push(props.job.weekendMinRate);
  }
  if (props.job.saturdayMinRate) {
    rateArray.push(props.job.saturdayMinRate);
  }
  if (props.job.saturdayMaxRate) {
    rateArray.push(props.job.saturdayMaxRate);
  }
  if (props.job.sundayMinRate) {
    rateArray.push(props.job.sundayMinRate);
  }
  if (props.job.sundayMaxRate) {
    rateArray.push(props.job.sundayMaxRate);
  }

  rateArray.sort((a, b) => a - b);
  const firstEl = rateArray.shift();
  const lastEl = rateArray.pop();
  return (
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(firstEl) +
    " - " +
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(lastEl)
  );
};

const showRateInfo = computed(() => {
  // always show rate info for pharmacy
  if (props.userRole === 'pharmacy') {
    return true;
  }
  // do not show rate for internal staff
  if (!props.job || props.job.isInternalStaffOfThisPharmacy || props.job.isVisibileAsInternalLocum !== false) {
    return false;
  }
  const shifts = props.shifts ?? props.job.shifts;
  if (props.userRole === "locum" && props.job.hideRecommendedRateForLocum === true) {
    return false;
  }
  return locumCanSeeJobRates(shifts, props.userRole, {
    shiftsComplete: props.shiftsComplete,
  });
});
const showTravelInfo = computed(() => {
  return props.job && props.job.settings.payingForTravel == 3 && props.job.maxTravelCompensation !== 0;
});
</script>

<style scoped lang="scss">
.job-about-collapse {
  border: 1px solid rgba(var(--locumate-clr-gray-light), 0.5);
  border-radius: var(--locumate-corner-radius, 8px);
  overflow: hidden;
  background-color: rgba(var(--locumate-secondary-color), 0.08);

  :deep(.el-collapse-item__header) {
    height: auto;
    min-height: 48px;
    padding: 12px 16px;
    border: 0;
    background-color: rgba(var(--locumate-secondary-color), 0.12);
    color: rgb(var(--locumate-secondary-color));
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
  }

  :deep(.el-collapse-item__wrap) {
    border-bottom: 0;
    background-color: #fff;
  }

  :deep(.el-collapse-item__content) {
    padding: 0;
  }
}

.more-info {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 24px;
  font-size: 14px;
  line-height: 28px;
  padding: 0;

  .collapse-item {
    display: grid;
    grid-column: span 2;
    grid-template-columns: subgrid;
    align-items: start;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(var(--locumate-clr-gray-light), 0.35);

    &:last-child {
      border-bottom: 0;
    }
  }

  .collapse-title {
    color: #71777d;
    font-size: 14px;
    line-height: 1.4;
  }

  .collapse-value {
    color: #3b445a;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.4;
    text-align: right;
    word-break: break-word;
  }

  .collapse-item--notes {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    .job-notes__content {
      margin: 0;
      padding: 0;
      color: #3b445a;
      font-size: 14px;
      line-height: 1.5;
      font-weight: 400;
      text-align: left;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
}
</style>
